import React, {useRef, Fragment, useState, useMemo, useCallback, useEffect} from 'react';
import Modal from '../../../../common/components/Modal/Modal';
import {labels} from '../../../../common/utils/utils';
import styled from 'styled-components';
import {useDispatch} from 'react-redux';
import {createDelegate, getUserById, removeDelegateUser} from '../../users';
import AddDelegate from '../DelegateAccess/AddDelegate/AddDelegate';
import LoadingIndicator from '@common/LoadingIndicator/loadingIndicator';
import {useSelector} from 'react-redux';
import TabBar from '../../../../common/components/TabBar/TabBar';
import ViewDelegate from './ViewDelegate/ViewDelegate';

function DelegateAccessModal(props) {
	const {showModal, setModalVisibility} = props;
	const dispatch = useDispatch();
	const user = useSelector((store) => store.user);

	const tabs = useMemo(() => {
		return [
			{
				id: '1',
				disabled: false,
				label: labels.delegateTo,
				value: 'delegatedTo',
				variant: 'primary'
			},
			{
				id: '2',
				disabled: false,
				label: labels.delegateFrom,
				value: 'delegatedFrom',
				variant: 'primary'
			}
		];
	}, [labels.delegateTo, labels.delegateFrom]);

	const [isLoading, setLoading] = useState(false);
	const [isDisable, setIsDisable] = useState(true);
	const [activeTab, setActiveTab] = useState('1');
	const [users, setUsers] = useState(user.delegatedTo);
	const [noDelegationMessage, setNoDelegationMessage] = useState(labels.noDelegatesAdded);

	const modalRef = useRef();

	const handleSaveDelegate = useCallback(
		(closeModal) => {
			if (activeTab !== '1') {
				return;
			}
			const isError = modalRef.current.validate();
			if (!isError) {
				setLoading(true);
				const users = modalRef.current.getUsers();
				if (users && users.length) {
					saveDelegate(users, closeModal);
				}
			}
		},
		[activeTab, modalRef, saveDelegate]
	);

	const saveDelegate = (users, closeModal = false) => {
		dispatch(createDelegate(users))
			.then(() => {
				dispatch(getUserById())
					.then(() => {
						setLoading(false);
						setModalVisibility(!closeModal);
						if (!closeModal) {
							modalRef.current.resetUserList();
						}
					})
					.catch(() => {
						setLoading(false);
					});
			})
			.catch(() => {
				setLoading(false);
			});
	};

	const comparer = useCallback((a, b) => {
		const name1 = `${a.firstName.trim()} ${a.lastName.trim()}`;
		const name2 = `${b.firstName.trim()} ${b.lastName.trim()}`;
		if (name1 > name2) {
			return 1;
		} else if (name1 < name2) {
			return -1;
		} else {
			return 0;
		}
	}, []);

	const onTabChange = useCallback(
		(selectedTab) => {
			setActiveTab(selectedTab);
			switch (selectedTab) {
				case '1': {
					const delegatedTo = [...user.delegatedTo];
					setUsers(delegatedTo.sort(comparer));
					setNoDelegationMessage(labels.noDelegatesAdded);
					break;
				}
				case '2': {
					const delegateFrom = [...user.delegatedFrom];
					setUsers(delegateFrom.sort(comparer));
					setNoDelegationMessage(labels.noDelegationReceived);
					break;
				}
			}
		},
		[user.delegatedTo, user.delegatedFrom, labels.noDelegatesAdded, labels.noDelegationReceived, comparer]
	);

	const removeDelegate = useCallback((toRemoveuser) => {
		setLoading(true);
		dispatch(removeDelegateUser(user.id, toRemoveuser.userId))
			.then(() => {
				dispatch(getUserById())
					.then(() => {
						setLoading(false);
					})
					.catch(() => {
						setLoading(false);
					});
			})
			.catch(() => {
				setLoading(false);
			});
	});

	const resetUsers = () => {
		let delegated = [];
		if (activeTab === '1') {
			if (Array.isArray(user.delegatedTo)) {
				delegated = [...user.delegatedTo];
			}
		} else {
			if (Array.isArray(user.delegatedFrom)) {
				delegated = [...user.delegatedFrom];
			}
		}
		setUsers(delegated.sort(comparer));
	};

	useEffect(() => {
		resetUsers();
	}, []);

	useEffect(() => {
		if (showModal) {
			resetUsers();
		}
	}, [showModal]);

	useEffect(() => {
		resetUsers();
	}, [user.delegatedTo]);

	const okButtonDisable = isLoading || isDisable || activeTab === '2';

	return (
		<Modal
			show={showModal}
			modalsize="small"
			showButtons
			confirmBtnTitle={labels.saveAndClose}
			closeBtnTitle={labels.closeLabel}
			title={labels.delegateAccess}
			onOkClick={() => handleSaveDelegate(true)}
			closeTitle={labels.closeLabel}
			isOkButtonDisabled={okButtonDisable}
			isCancelButtonDisabled={isLoading}
			showSaveAndCreateNewButton
			saveAndCreateNewBtnTitle={labels.save}
			isSaveAndCreateNewButtonDisabled={okButtonDisable}
			onSaveAndCreateNew={() => handleSaveDelegate(false)}
			onHide={() => {
				setModalVisibility(false);
				setLoading(false);
			}}
		>
			<StyledModalBody className="styledModalBody customScrollbar">
				<LoadingIndicator show={isLoading} />
				<TabBar activeTab={activeTab} onTabChange={onTabChange} tabs={tabs} styleName="tab-bar" />
				{activeTab === '1' ? (
					<Fragment>
						<section className="inner-modal-header">
							<p className="delegate-instruction">{labels.delegateToInstruction}</p>
						</section>
						<AddDelegate setLoading={setLoading} ref={modalRef} setIsDisable={setIsDisable}>
							{users && users.length > 0 ? (
								<ViewDelegate key={activeTab} activeTab={activeTab} users={users} removeDelegate={removeDelegate} noDelegationMessage={noDelegationMessage} />
							) : null}
						</AddDelegate>
					</Fragment>
				) : (
					<Fragment>
						<section className="inner-modal-header">
							<p className="delegate-instruction">{labels.delegateFromInstruction}</p>
						</section>
						<ViewDelegate key={activeTab} activeTab={activeTab} users={users} removeDelegate={removeDelegate} noDelegationMessage={noDelegationMessage} />
					</Fragment>
				)}
			</StyledModalBody>
		</Modal>
	);
}

const StyledModalBody = styled.section`
	min-height: var(--px-560);
	gap: var(--px-24);
	display: flex;
	flex-direction: column;
	.motif-progress-loader {
		position: absolute;
		top: var(--px-60);
		left: 50%;
	}
	.inner-modal-header {
		.delegate-instruction {
			font-size: var(--px-14);
			font-weight: 300;
			margin: 0;
			line-height: var(--px-20);
			color: var(--neutrals-900);
		}
	}
	.show-delegates {
		margin: var(--px-30) 0 var(--px-10) 0;
		font-size: var(--px-18);
		font-weight: bold;
		color: var(--neutrals-900);
	}
	.add-delegate-list {
		display: flex;
		flex-direction: column;
		gap: var(--px-24);

		.addDelegateBtn {
			width: var(--px-140);
			gap: var(--px-4);
		}

		ul {
			li {
				display: flex;
				.userfield-row {
					.user-selectbox {
						position: relative;
						.select-dropdown {
							.react-select-container {
								.react-select__menu {
									position: relative;
									top: 0;
								}
							}
						}
						.clearall {
							display: inline-flex;
							position: absolute;
							right: var(--px-10);
							align-items: center;
							margin: 0;
						}
					}
				}
				.remove-delegate-button {
					height: var(--px-50);
					margin: 0 0 0 var(--px-10);
					display: flex;
					align-items: center;
					justify-content: flex-start;
					svg {
						fill: var(--neutrals-1000);
					}
				}
				.react-select__indicator {
					display: none;
				}
			}
		}
	}
`;

export default DelegateAccessModal;
